import React, { useState, useEffect } from 'react';

const Subscribe = () => {
  const [email, setEmail] = useState('');
  const [alertLevel, setAlertLevel] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [threats, setThreats] = useState([]);
  const [stats, setStats] = useState({
    activeThreats: 0,
    blockedAttacks: 0,
    protectedSystems: 0
  });

  // GLOCYBS color scheme
  const colors = {
    primary: '#1e40af', // Blue
    secondary: '#7c3aed', // Purple
    accent: '#059669', // Emerald
    warning: '#f59e0b', // Amber
    danger: '#dc2626', // Red
    dark: '#1f2937', // Gray-800
    light: '#f8fafc' // Slate-50
  };

  // Threat simulation data
  const threatTypes = [
    { type: 'Malware Detection', severity: 'HIGH', icon: '🦠' },
    { type: 'Phishing Attempt', severity: 'MEDIUM', icon: '🎣' },
    { type: 'DDoS Attack', severity: 'CRITICAL', icon: '⚡' },
    { type: 'Data Breach', severity: 'CRITICAL', icon: '🔓' },
    { type: 'Ransomware', severity: 'HIGH', icon: '🔒' },
    { type: 'SQL Injection', severity: 'MEDIUM', icon: '💉' },
    { type: 'XSS Attack', severity: 'LOW', icon: '🕷️' },
    { type: 'Brute Force', severity: 'MEDIUM', icon: '🔨' }
  ];

  // Generate live threat feed
  useEffect(() => {
    const generateThreat = () => {
      const threat = threatTypes[Math.floor(Math.random() * threatTypes.length)];
      const locations = ['US-East', 'EU-West', 'Asia-Pacific', 'Americas', 'EMEA'];
      
      return {
        id: Date.now() + Math.random(),
        ...threat,
        location: locations[Math.floor(Math.random() * locations.length)],
        timestamp: new Date().toLocaleTimeString(),
        blocked: Math.random() > 0.3
      };
    };

    const interval = setInterval(() => {
      setThreats(prev => {
        const newThreat = generateThreat();
        return [newThreat, ...prev.slice(0, 9)]; // Keep only last 10 threats
      });

      // Update stats
      setStats(prev => ({
        activeThreats: Math.floor(Math.random() * 50) + 10,
        blockedAttacks: prev.blockedAttacks + Math.floor(Math.random() * 3),
        protectedSystems: 1247 + Math.floor(Math.random() * 100)
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setMessage('Please enter a valid email address');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      // Simulate API call - replace with actual backend
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // For demo purposes, always succeed
      setMessage('Successfully subscribed to GLOCYBS threat alerts!');
      setMessageType('success');
      setEmail('');
      setAlertLevel('all');
    } catch (error) {
      setMessage('Failed to subscribe. Please try again.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-50 border-red-200';
      case 'HIGH': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'MEDIUM': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'LOW': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
     

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Sidebar - Threat Feed */}
          <div className="lg:col-span-3 space-y-6">
            {/* Live Stats */}
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-slate-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Live Security Stats
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Active Threats</span>
                  <span className="font-bold text-red-600">{stats.activeThreats}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Blocked Today</span>
                  <span className="font-bold text-green-600">{stats.blockedAttacks}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Protected Systems</span>
                  <span className="font-bold text-blue-600">{stats.protectedSystems}</span>
                </div>
              </div>
            </div>

            {/* Threat Feed */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-slate-100 px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-bold text-gray-800">Global Threat Feed</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {threats.map((threat) => (
                  <div key={threat.id} className="p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors animate-fade-in">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-2">
                        <span className="text-lg">{threat.icon}</span>
                        <div>
                          <div className="text-sm font-medium text-gray-800">{threat.type}</div>
                          <div className="text-xs text-gray-500">{threat.location} • {threat.timestamp}</div>
                        </div>
                      </div>
                      <div className={`px-2 py-1 text-xs font-bold rounded-full border ${getSeverityColor(threat.severity)}`}>
                        {threat.severity}
                      </div>
                    </div>
                    {threat.blocked && (
                      <div className="mt-2 text-xs text-green-600 font-medium">✓ Blocked by GLOCYBS</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-6">
            <div className="text-center mb-12">
              {/* Hero Section */}
              <div className="mb-8 animate-fade-in">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-6 animate-bounce">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                <h1 className="text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-700 mb-4 animate-fade-in-up">
                  Stay Protected
                </h1>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 animate-fade-in-up animation-delay-200">
                  Real-Time <span className="text-red-600">Threat Alerts</span>
                </h2>
                
                <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animation-delay-400">
                  Subscribe to GLOCYBS advanced threat intelligence and get instant notifications about cybersecurity risks targeting your industry.
                </p>
              </div>

              {/* Subscription Form */}
              <div className="bg-gradient-to-br from-white to-slate-50 rounded-3xl border border-slate-200 p-8 lg:p-10 shadow-xl animate-fade-in animation-delay-600">
                <div className="space-y-6">
                  
                  {/* Email Input */}
                  <div className="text-left">
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Professional Email Address
                    </label>
                    <div className="relative group">
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full px-4 py-4 bg-white border-2 border-slate-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 group-hover:border-slate-300"
                        required
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Alert Level Selection */}
                  <div className="text-left">
                    <label htmlFor="alertLevel" className="block text-sm font-semibold text-gray-700 mb-2">
                      Alert Preferences
                    </label>
                    <div className="relative group">
                      <select
                        id="alertLevel"
                        value={alertLevel}
                        onChange={(e) => setAlertLevel(e.target.value)}
                        className="w-full px-4 py-4 bg-white border-2 border-slate-200 rounded-xl text-gray-800 focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 group-hover:border-slate-300 appearance-none cursor-pointer"
                      >
                        <option value="all">All Alert Levels</option>
                        <option value="critical">Critical Alerts Only</option>
                        <option value="high">High & Critical</option>
                        <option value="medium">Medium & Above</option>
                        <option value="low">All Severity Levels</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Alert Level Preview */}
                  {alertLevel !== 'all' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 animate-fade-in">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-semibold text-blue-800">
                          You'll receive {alertLevel.toUpperCase()} priority alerts
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full px-8 py-4 font-bold rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg focus:outline-none focus:ring-4 ${
                      isSubmitting
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-purple-700 text-white hover:from-blue-700 hover:to-purple-800 hover:shadow-xl focus:ring-blue-500/30'
                    }`}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>Subscribing...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>Subscribe to Alerts</span>
                      </div>
                    )}
                  </button>

                  {/* Message Display */}
                  {message && (
                    <div className={`p-4 rounded-xl border-2 animate-fade-in ${
                      messageType === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                      <div className="flex items-center space-x-2">
                        {messageType === 'success' ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        <span className="font-semibold">{message}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar - Features */}
          <div className="lg:col-span-3 space-y-6">
            {/* Trust Indicators */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Why Choose GLOCYBS?</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Real-Time Detection</h4>
                    <p className="text-sm text-gray-600">Quantum-powered threat analysis</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Zero-Trust Architecture</h4>
                    <p className="text-sm text-gray-600">Enterprise-grade security</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">AI Intelligence</h4>
                    <p className="text-sm text-gray-600">Machine learning threat prediction</p>
                  </div>
                </div>
              </div>
            </div>

            {/* GLOCYBS Products */}
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 border border-purple-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Our Solutions</h3>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <h4 className="font-semibold text-purple-700">BIQUA</h4>
                  <p className="text-sm text-gray-600">Quantum threat detection</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <h4 className="font-semibold text-purple-700">G-FLEA</h4>
                  <p className="text-sm text-gray-600">Advanced firewall protection</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <h4 className="font-semibold text-purple-700">QUBISA</h4>
                  <p className="text-sm text-gray-600">Security automation platform</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

     

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
      `}</style>
    </div>
  );
};

export default Subscribe;